<template>
  <RouterLink
    :class="classes"
    :to="to"
    :style="itemStyle"
    @click.native="onClick"
  >
    <Icon :class="`nav-item__image${expanded ? ' nav-item__image--expanded' : ''}`">{{ path }}</Icon>
    <p v-if="expanded" class="nav-item__title" :style="titleStyle">
      {{ title }}
    </p>
    <span
      v-if="showNumberOfPendingActions &&
        pageId === pendingActionsPageId &&
        (numberOfPendingActions > 0 || numberOfPendingActions === '99+')"
      class="nav-item__actions-pending-background__counter"
    >
      {{ numberOfPendingActions }}
    </span>
  </RouterLink>
</template>

<script>
/**
 * Navigation link
 *
 * @vue-prop {Number} pageId - Id of the page.
 * @vue-prop {Object} pageOption - Custom params from navigation menu settings.
 * @vue-prop {String} title - Text displayed on the link.
 * @vue-prop {String} to - Denotes the target route of the link.
 * @vue-prop {Object} [exact] - List of queries that should be exact according to an URL querystring
 *                              for the link to be highlighted.
 * @vue-prop {String} [imgSrc=DEFAULT_IMGSRC] - Source of the icon of the link.
 * @vue-prop {Boolean} [min] - Toggle on/off square-shaped link (NavBar items).
 * @vue-prop {Boolean} drawer - Toggle if the menu is for the navigation drawer or not
 */
import { mapState } from 'vuex';
import {
  SET_ACTIVE_ROUTE_CLICKED,
  SET_ACTIVE_ROUTE_MATCHED,
  GET_PENDING_ACTIONS_NUMBER,
} from '@/store/mutation-types';
import {
  LOGOUT,
  FETCH_PENDING_ACTIONS_NUMBER,
} from '@/store/action-types';
// Components
import Icon from '@/components/icon.vue';
import { pageMedium } from '@/assets/icons';
// Import socket handling utils
import SocketMessages from '@/utils/classes/SocketMessages';
// Utils
import { SColors } from '@/utils/constants';
import { getTextFgColor, isColorDark } from '@/utils/main';
import SPage from '@/utils/classes/SPage';
// Mixins
import mixinSocketHandler from '@/mixins/socket-handler.vue';

const DEFAULT_IMGPATH = pageMedium;

function isRouteEqual(lhsRoute, rhsRoute) {
  return lhsRoute && rhsRoute && lhsRoute.path === rhsRoute.path
    && Object.keys(lhsRoute.query).length === Object.keys(rhsRoute.query).length
    && Object.keys(lhsRoute.query).every(key => String(lhsRoute.query[key]) === String(rhsRoute.query[key]));
}

export default {

  name: 'NavItem',

  components: {
    Icon,
  },

  mixins: [mixinSocketHandler],

  props: {
    pageId: {
      type: Number,
      default: 0,
    },
    pageOptions: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: '',
    },
    to: {
      type: String,
      default: '',
    },
    exact: {
      type: Object,
      default: () => ({}),
    },
    path: {
      type: String,
      default: DEFAULT_IMGPATH,
    },
    isTitleFull: Boolean,
    bgColor: {
      type: String,
      default: 'transparent',
    },
    expanded: Boolean,
    drawer: Boolean,
  },

  data() {
    return {
      pendingActionsPageId: SPage.PENDING.id,
    };
  },

  computed: {
    classes() {
      return {
        'nav-item': !this.drawer,
        'nav-item--drawer': this.drawer,
        'nav-item--expanded': this.expanded && !this.drawer,
        'nav-item--current': this.isActiveRoute && this.isSimilarRoute,
      };
    },
    route() {
      return this.$router.match(this.to);
    },
    isSimilarRoute() {
      return this.route.path === this.$route.path
        && Object.keys(this.exact).every(key => String(this.exact[key]) === String(this.$route.query[key]));
    },
    isSameRoute() {
      return isRouteEqual(this.route, this.$route);
    },
    isActiveRoute() {
      return isRouteEqual(this.route, this.activeRoute);
    },
    /** String: Dislay the number of pending actions. If more than 99, displays 99+ */
    numberOfPendingActions() {
      if (this.pendingActionsNumber) {
        return this.pendingActionsNumber > 99 ? '99+' : this.pendingActionsNumber;
      }
      return 0;
    },
    showNumberOfPendingActions() {
      return this.pageOptions && this.pageOptions.displayNumberOfPendingActions;
    },
    ...mapState({
      activeRoute: state => state.activeRouteMatched || state.activeRouteClicked,
    }),
    /* Creating css vars to display the right colors in the whole nav-item */
    itemStyle() {
      if (!this.drawer) {
        const notTrans = this.bgColor && this.bgColor !== 'transparent';
        return {
          '--bg-color': this.bgColor || 'transparent',
          '--font-color': notTrans ? getTextFgColor(this.bgColor) : '#fff',
          /* Define the background color when item is hovered,
        darkens if font color is white, lightens if font color is black.
        The two lasts digits define the opacity of the background */
          '--bg-hover': `${notTrans ? this.bgColor : SColors.mainTextColor}`,
          '--font-hover': getTextFgColor(notTrans ? this.bgColor : SColors.mainTextColor),
          '--bg-brightness': `${isColorDark(notTrans ? this.bgColor : SColors.mainTextColor) ? '140%' : '60%'}`,
        };
      }
      return {
        '--bg-color': '#000',
        '--font-color': '#fff',
        '--font-hover': '#fff',
        '--bg-hover': '#000',
      };
    },
    titleStyle() {
      /* Checking if the user choose to fully display navItem's title or not and displaying title style */
      if (this.isTitleFull || this.drawer) {
        return {
          'word-break': 'break-word',
        };
      }
      return {
        'white-space': 'nowrap',
        'text-overflow': 'ellipsis',
      };
    },
    ...mapState({
      pendingActionsNumber: state => state[GET_PENDING_ACTIONS_NUMBER.stateKey] || [],
      pendingActionsNumberLoading: state => state[GET_PENDING_ACTIONS_NUMBER.loadingKey],
    }),
  },

  watch: {
    isSameRoute() {
      this.$store.commit(SET_ACTIVE_ROUTE_MATCHED, this.route);
    },
    showNumberOfPendingActions(newVal) {
      if (newVal) {
        this.$store.dispatch(FETCH_PENDING_ACTIONS_NUMBER);
      }
    },
  },

  created() {
    if (this.isActiveRoute) {
      this.$emit('sameRoute', this.route);
    }
    if (this.pageId === this.pendingActionsPageId) {
      if (!this.pendingActionsNumberLoading && this.showNumberOfPendingActions) {
        this.$store.dispatch(FETCH_PENDING_ACTIONS_NUMBER);
      }
      this.handleSocketMessage(SocketMessages.CC_MSGS.PENDING_ACTIONS_NUMBER, () => {
        if (!this.pendingActionsNumberLoading) {
          this.$store.dispatch(FETCH_PENDING_ACTIONS_NUMBER);
        }
      });
    }
  },

  methods: {
    onClick() {
      this.$store.commit(SET_ACTIVE_ROUTE_CLICKED, this.route);
      if (this.to === '/logout') {
        this.$store.dispatch(LOGOUT, { app: this });
      }
    },
  },
};
</script>>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.nav-item {
  $nav-item-padding: 0.9rem;
  $nav-item-text-height: 1.15rem;
  transition: background-color 0.3s ease, filter 0.3s ease;
  background-color: var(--bg-color);
  margin: 0.5em 0.2em;
  position: relative;
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 0 $nav-item-padding;
  outline: none;
  user-select: none;
  -webkit-touch-callout: none;
  border-radius: $main-border-radius;
  line-height: $nav-item-text-height;
  height: calc(#{$nav-item-padding * 2} + #{$nav-item-text-height});

  &:hover, &--current {
    filter: brightness(var(--bg-brightness));
    background-color: var(--bg-hover) !important;

    & > p, svg {
      color: var(--font-hover) !important;
    }
  }

  &--drawer {
    transition: background-color 0.3s ease;
    padding: 1em 2em;
    align-items: center;
    cursor: pointer;
    display: flex;
    justify-content: flex-start;

    & > * {
      color: white;
    }
  }

  &__title {
    overflow: hidden;
    width: 80%;
  }

  &__title, svg {
    color: var(--font-color);
  }

  &--expanded {
    justify-content: flex-start;
  }

  &__image {
    width: 1.1rem;
    height: 1.1rem;

    &--expanded {
      // margin-left: 0.5em;
      margin-right: 0.9rem;
    }
  }

  &__actions-pending-background {

    &__counter {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 40%;
      background-color: #2a5a91;
      width: 30px;
      height: 25px;
      margin-right: -5px;
      font-size: 0.75em;
      font-weight: bold;
      color: white;
    }
  }
}


@media (max-height: $smartphone-portrait) {
  .nav-item__image {
    width: 22px;
  }
}

@media (max-width: $smartphone-landscape) {
  .nav-item {
    padding: 0 0.4rem;

    &__image {
      width: 18px;
    }

    &__actions-pending-background__counter {
      display: none;
    }
  }
}
</style>
